import {
	BusyIndicator,
	Tabs
} from "client/Components";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../SettingsPage";
import { ensureAnalyticsEnabled } from "client/Api/Analytics/analyticsStatistics";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AppsAndUsersTab } from "./AppsAndUsersTab";
import { HubTab } from "./HubTab";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const AnalyticsPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [shouldFetchStatistics, setShouldFetchStatistics] = useState<boolean>(false);
	const [selectedDaysRange, setSelectedDaysRange] = useState<number>(1);
	const [tabId, setTabId] = useState<StatisticsTabId>("hub");

	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.Analytics);

	useEffect(
		(): EffectWithDestructorResult => {
			async function checkIfAnalyticsIsEnabled(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					await ensureAnalyticsEnabled(signal)
						.catch(handlePageDataLoadingError);

					setShouldFetchStatistics(true);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(
						localize("_GeneralErrorMessage")
					);
				}
			}

			const cancellation = new AbortController();

			checkIfAnalyticsIsEnabled(cancellation.signal);
			return () => cancellation.abort();

		},
		[alertManager]
	);

	return (
		<SettingsPage
			title={localize("_Analytics")}
		>
			<SettingsPage.Section height="stretch">
				{isBusy && (
					<BusyIndicator />
				)}
				{shouldFetchStatistics && (
					<Tabs<StatisticsTabId>
						activeTabId={tabId}
						onActiveTabIdChange={setTabId}
					>
						<Tabs.Tab<StatisticsTabId>
							id="hub"
							title={localize("_TheHub")}
						>
							{tabId === "hub" && (
								<HubTab
									selectedDaysRange={selectedDaysRange}
									onSelectedDaysRangeChange={setSelectedDaysRange}
								/>
							)}
						</Tabs.Tab>
						<Tabs.Tab<StatisticsTabId>
							id="apps-and-users"
							title={localize("_AppsAndUsers")}
						>
							{tabId === "apps-and-users" && (
								<AppsAndUsersTab
									selectedDaysRange={selectedDaysRange}
									onSelectedDaysRangeChange={setSelectedDaysRange}
								/>
							)}
						</Tabs.Tab>
					</Tabs>
				)}
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
AnalyticsPage.displayName = "AnalyticsPage";

type StatisticsTabId = "hub" | "apps-and-users";

export const AnalyticsRouting = {
	page: "/Analytics"
};
